/**
 * Optional "it's your turn" chime, played once on promotion.
 *
 * OFF BY DEFAULT. Nothing here runs unless a template supplies a sound URL
 * (`promotedSound` in the crowdhandler-data JSON), so existing templates —
 * including every forked customer template — are completely unaffected.
 *
 * Why this is more than `new Audio(url).play()`:
 *
 *  1. Autoplay policy. Browsers refuse audible playback without a user gesture
 *     in the document. Waiting room visitors arrive by redirect and usually
 *     never click anything, so an unprompted play() rejects with
 *     NotAllowedError. `unlock()` must therefore be called from inside a real
 *     gesture handler; it primes the element while muted, which is permitted,
 *     and leaves it ready to play audibly later.
 *
 *  2. The redirect races the sound. handlePromoted navigates away immediately,
 *     which kills playback mid-note. `play()` resolves when the clip ends OR
 *     when a cap expires, so the caller can hold the redirect for exactly as
 *     long as it's worth holding and no longer.
 */

export interface PromotedSound {
    /** True once a gesture has primed the element for audible playback. */
    readonly unlocked: boolean;
    /**
     * Prime for later playback. MUST be called synchronously from a user
     * gesture handler (click/keydown), or the browser will reject it.
     * Resolves true if the element is ready.
     */
    unlock: () => Promise<boolean>;
    /**
     * Play once. Resolves when playback finishes or `capMs` elapses —
     * whichever is first — so a stalled clip can never block a redirect.
     * Resolves false if it didn't play at all.
     */
    play: (capMs?: number) => Promise<boolean>;
    /** Release the element and drop listeners. */
    dispose: () => void;
}

/** Longest we'll ever hold a redirect waiting for audio. */
const DEFAULT_CAP_MS = 1600;

export function createPromotedSound(url: string): PromotedSound | null {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return null;
    if (!url) return null;

    let el: HTMLAudioElement | null = new Audio();
    let unlocked = false;

    el.src = url;
    el.preload = 'auto';
    // Decoded ahead of promotion: fetching at promotion time would add latency
    // to a window that may be zero.
    el.load();

    const api: PromotedSound = {
        get unlocked() { return unlocked; },

        async unlock() {
            if (!el) return false;
            if (unlocked) return true;
            try {
                // Muted playback is allowed without a gesture on most engines,
                // but doing it *inside* one also satisfies the strict ones and
                // marks the element as user-activated for later audible use.
                el.muted = true;
                await el.play();
                el.pause();
                el.currentTime = 0;
                el.muted = false;
                unlocked = true;
                return true;
            } catch (e) {
                // Left locked; caller can retry on the next gesture.
                el.muted = false;
                return false;
            }
        },

        play(capMs = DEFAULT_CAP_MS) {
            if (!el) return Promise.resolve(false);

            return new Promise<boolean>((resolve) => {
                const audio = el as HTMLAudioElement;
                let settled = false;
                const done = (played: boolean) => {
                    if (settled) return;
                    settled = true;
                    audio.removeEventListener('ended', onEnded);
                    clearTimeout(timer);
                    resolve(played);
                };
                const onEnded = () => done(true);

                // The cap is the load-bearing part: without it a clip that
                // never fires `ended` (stalled network, tab suspended, silent
                // switch on iOS) would hold the redirect open indefinitely.
                const timer = setTimeout(() => done(true), capMs);

                audio.addEventListener('ended', onEnded);

                try {
                    audio.currentTime = 0;
                } catch (e) {
                    /* not seekable yet — play from wherever it is */
                }

                const p = audio.play();
                if (p && typeof p.catch === 'function') {
                    p.catch(() => done(false));   // blocked or failed: don't stall
                }
            });
        },

        dispose() {
            if (!el) return;
            try {
                el.pause();
                el.src = '';
            } catch (e) { /* nothing useful to do */ }
            el = null;
            unlocked = false;
        }
    };

    return api;
}
